// store/slices/userInputSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types
export interface PassengerDetails {
  gender: "Male" | "Female" | "Other";
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
}

export interface ContactDetailsI {
  mobileNo: number;
  emailId: string;
}

export interface BillingDetailsI {
  address: string;
  pincode: string;
  state: string;
  country: string;
  city: string;
}

export interface TravellerDetailsI {
  personal: boolean;
  business: boolean;
  passengerDetails: PassengerDetails[];
  passportDetails: PassportDetails;
  getBookingDetails: boolean;
  contactDetails: ContactDetailsI;
  billingDetails: BillingDetailsI;
}

export interface Meal {
  code: string;
  amount: number;
  desc: string;
  id: string;
  value: string;
  increment: number;
  count?: number;
}

interface PassportDetails {
  nationality: string;
  passportNumber: string;
  passportExpiry: string;
  passportIssueCountry: string;
  passportIssueDate: string;
}

export interface UserInputState {
  selectedFlight: {};
  selectedReturnFlight: {};
  selectedBucket: {};
  travellerDetails: TravellerDetailsI | {};
  meal: Meal[];
  baggageDetails: any;
  seatDetails: any;
}

// Initial state
const initialState: UserInputState = {
  selectedFlight: {},
  selectedReturnFlight: {},
  selectedBucket: {},
  travellerDetails: {},
  meal: [],
  baggageDetails: {},
  seatDetails: {},
};

// Register Form
export interface UserRegisterForm {
  gender: string;
  firstName: string;
  lastname: string;
  mobile: string;
  email: string;
  password: string;
}

// Create slice
const userInputSlice = createSlice({
  name: "userInput",
  initialState,
  reducers: {
    setSelectedFlight: (state, action: PayloadAction<any>) => {
      state.selectedFlight = action.payload;
    },
    setSelectedReturnFlight: (state, action: PayloadAction<any>) => {
      state.selectedReturnFlight = action.payload;
    },
    setSelectedBucket: (state, action: PayloadAction<any>) => {
      state.selectedBucket = action.payload;
    },
    setTravellerDetails: (state, action: PayloadAction<TravellerDetailsI>) => {
      state.travellerDetails = action.payload;
    },
    updateMeal: (state, action: PayloadAction<Meal>) => {
      const existingIndex = state.meal.findIndex(
        (m) => m.id === action.payload.id
      );

      if (existingIndex >= 0) {
        const newCount =
          (state.meal[existingIndex].count || 0) + action.payload.increment;

        if (newCount <= 0) {
          state.meal = state.meal.filter((_, index) => index !== existingIndex);
        } else {
          state.meal[existingIndex].count = newCount;
        }
      } else {
        state.meal.push({ ...action.payload, count: 1 });
      }
    },
    setBaggageDetails: (state, action: PayloadAction<any>) => {
      state.baggageDetails = action.payload;
    },
    setSeatDetails: (state, action: PayloadAction<any>) => {
      state.seatDetails = action.payload;
    },
    // Hydrate state from sessionStorage
    hydrateState: (state) => {
      if (typeof window !== "undefined") {
        const localData = sessionStorage.getItem("USER_INPUT");
        const selectedBucket = sessionStorage.getItem("SELECTED_BUCKET");

        if (localData && Object.keys(state.selectedBucket).length === 0) {
          state.selectedFlight = JSON.parse(localData);
        }

        if (selectedBucket && Object.keys(state.selectedFlight).length === 0) {
          state.selectedBucket = JSON.parse(selectedBucket);
        }
      }
    },

    clearLocalState: (state, action: PayloadAction<any>) => {
      if (action.payload in state) {
        state[action.payload as keyof UserInputState] = {} as any;
      }
      return state;
    },

    resetState: (state) => {
      // Properly reset the state by mutating individual properties
      state.selectedFlight = {};
      state.selectedReturnFlight = {};
      state.selectedBucket = {};
      state.travellerDetails = {};
      state.meal = [];
      state.baggageDetails = {};
      state.seatDetails = {};
      
      if (typeof window !== "undefined") {
        sessionStorage.setItem("USER_INPUT", JSON.stringify({
        selectedFlight: {},
        selectedReturnFlight: {},
        selectedBucket: {},
        travellerDetails: {},
        meal: [],
        baggageDetails: {},
        seatDetails: {},
        }));
      }
    },
  },
});

// Export actions
export const {
  setSelectedFlight,
  setSelectedReturnFlight,
  setSelectedBucket,
  setTravellerDetails,
  updateMeal,
  setBaggageDetails,
  setSeatDetails,
  hydrateState,
  clearLocalState,
  resetState,
} = userInputSlice.actions;

// Export reducer
export default userInputSlice.reducer;
